import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./components/Login";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import Surveys from "./components/Surveys";
import Questions from "./components/Questions";
import Entities from "./components/Entities";
import Codes from "./components/Codes";
import Export from "./components/Export";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import QuestionsSpecificRoute from "./components/QuestionsSpecific";
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // Los datos siempre están stale - siempre se refetchean
      gcTime: 0, // No mantener en caché - siempre recargar desde 0
      refetchOnMount: 'always', // Siempre refrescar al montar
      refetchOnWindowFocus: false, // No refrescar al enfocar la ventana
      refetchOnReconnect: true, // Refrescar al reconectar
    },
  },
});

// Handler simple que invalida TODAS las queries cuando cambia la ruta
function NavigationHandler() {
  const queryClient = useQueryClient();
  const location = useLocation();

  useEffect(() => {
    // Invalidar TODAS las queries cada vez que cambia la ruta
    queryClient.invalidateQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.key]);

  return null;
}

function AppContent() {
  const { user, language, setLanguage, login, isLoading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  
  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div
            role="status"
            aria-label="loading"
            className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"
          ></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Show login if user is not authenticated
  if (!user) {
    return (
      <Login
        onLogin={login}
        language={language}
        onLanguageChange={setLanguage}
      />
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <NavigationHandler />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard key={location.pathname + location.key} />} />
          <Route path="/surveys/:id/edit" element={<Surveys key={location.pathname + location.key} />} />
          <Route path="/surveys/:id" element={<Surveys key={location.pathname + location.key} />} />
          <Route path="/surveys" element={<Surveys key={location.pathname + location.key} />} />
          <Route path="/questions" element={<Questions key={location.pathname + location.key} />} />
          <Route path="/questions-specific" element={<QuestionsSpecificRoute key={location.pathname + location.key} />} />
          <Route path="/entities/:id/edit" element={<Entities key={location.pathname + location.key} />} />
          <Route path="/entities/:id" element={<Entities key={location.pathname + location.key} />} />
          <Route path="/entities" element={<Entities key={location.pathname + location.key} />} />
          <Route path="/codes" element={<Codes key={location.pathname + location.key} />} />
          <Route path="/export" element={<Export key={location.pathname + location.key} />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;
